import { useState, useMemo } from "react";
import {
  Plus,
  Trash2,
  Headphones,
  UserPlus,
  Radio,
  Users,
  Download,
  Shield,
  Mic,
  Volume2,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PersonHoverCard } from "@/components/people/PersonHoverCard";
import {
  useProjectIntercomChannels,
  useProjectIntercomAssignments,
  useProjectIntercomProfiles,
  useCreateIntercomChannel,
  useUpdateIntercomChannel,
  useDeleteIntercomChannel,
  useCreateIntercomAssignment,
  useUpdateIntercomAssignment,
  useDeleteIntercomAssignment,
  useCreateIntercomProfile,
  useUpdateIntercomProfile,
  useDeleteIntercomProfile,
  useBulkAssignIntercomProfile,
  type IntercomChannel,
  type IntercomAssignment,
  type IntercomProfile,
} from "@/hooks/useProjectIntercom";
import { useProjectAvailability } from "@/hooks/useProjectPeople";
import { useProjectContacts } from "@/hooks/useProjectContacts";
import { useSingleStudio } from "@/hooks/useStudios";
import { apiFetch } from "@/lib/api-client";

interface IntercomTabProps {
  projectId: string;
  studioId?: string | null;
}

interface BulkCandidate {
  personID: string;
  firstName: string;
  lastName: string;
  unityUsername: string | null;
  source: "crew" | "contact";
}

function getAssignmentSortName(a: IntercomAssignment): string {
  if (a.person) {
    return `${a.person.lastName} ${a.person.firstName}`.toLowerCase();
  }
  return (a.displayName || a.unityUsername || "").toLowerCase();
}

// --- Bulk Add Dialog ---

function BulkAddDialog({
  projectID,
  existingPersonIDs,
  profiles,
}: {
  projectID: string;
  existingPersonIDs: Set<string>;
  profiles: IntercomProfile[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [profileID, setProfileID] = useState<string | null>(null);
  const createAssignment = useCreateIntercomAssignment();
  const { data: availability } = useProjectAvailability(projectID);
  const { data: contacts } = useProjectContacts(projectID);

  const candidates = useMemo(() => {
    const seen = new Set<string>();
    const result: BulkCandidate[] = [];

    for (const a of availability ?? []) {
      if (existingPersonIDs.has(a.person_id) || seen.has(a.person_id)) continue;
      seen.add(a.person_id);
      result.push({
        personID: a.person_id,
        firstName: a.person?.first_name ?? "",
        lastName: a.person?.last_name ?? "",
        unityUsername: a.person?.unity_username ?? null,
        source: "crew",
      });
    }

    for (const c of contacts ?? []) {
      if (
        !c.person ||
        existingPersonIDs.has(c.person_id) ||
        seen.has(c.person_id)
      )
        continue;
      const avail = (availability ?? []).find(
        (a) => a.person_id === c.person_id,
      );
      seen.add(c.person_id);
      result.push({
        personID: c.person_id,
        firstName: c.person.first_name,
        lastName: c.person.last_name,
        unityUsername: avail?.person?.unity_username ?? null,
        source: "contact",
      });
    }

    return result
      .filter((p) => {
        if (!search) return true;
        const term = search.toLowerCase();
        return (
          p.firstName.toLowerCase().includes(term) ||
          p.lastName.toLowerCase().includes(term) ||
          (p.unityUsername?.toLowerCase().includes(term) ?? false)
        );
      })
      .sort((a, b) =>
        `${a.lastName} ${a.firstName}`.localeCompare(
          `${b.lastName} ${b.firstName}`,
        ),
      );
  }, [availability, contacts, existingPersonIDs, search]);

  const toggleOne = (personID: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(personID)) next.delete(personID);
      else next.add(personID);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === candidates.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(candidates.map((c) => c.personID)));
    }
  };

  const handleBulkAdd = () => {
    const toAdd = candidates.filter((c) => selected.has(c.personID));
    for (const person of toAdd) {
      createAssignment.mutate({
        projectID,
        personID: person.personID,
        profileID,
        unityUsername: person.unityUsername,
        displayName: `${person.firstName} ${person.lastName}`,
      });
    }
    setSelected(new Set());
    setProfileID(null);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setSearch("");
          setSelected(new Set());
          setProfileID(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Users className="h-4 w-4 mr-1" />
          Bulk Add
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Project People</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="max-h-72 overflow-y-auto border rounded-md">
          {candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No people with Unity usernames available to add
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      checked={
                        candidates.length > 0 &&
                        selected.size === candidates.length
                      }
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Username</TableHead>
                  <TableHead>Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((c) => (
                  <TableRow
                    key={c.personID}
                    className="cursor-pointer"
                    onClick={() => toggleOne(c.personID)}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selected.has(c.personID)}
                        onCheckedChange={() => toggleOne(c.personID)}
                      />
                    </TableCell>
                    <TableCell>
                      {c.firstName} {c.lastName}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {c.unityUsername ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground capitalize">
                      {c.source}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        {profiles.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Assign profile:
            </span>
            <Select
              value={profileID ?? "__none__"}
              onValueChange={(v) => setProfileID(v === "__none__" ? null : v)}
            >
              <SelectTrigger className="h-8">
                <SelectValue placeholder="No profile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No profile</SelectItem>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-sm text-muted-foreground">
            {selected.size} selected
          </span>
          <Button
            onClick={handleBulkAdd}
            disabled={selected.size === 0 || createAssignment.isPending}
          >
            Add {selected.size} User{selected.size !== 1 ? "s" : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Add Single User Dialog ---

function AddSingleUserDialog({
  projectID,
  existingUsernames,
}: {
  projectID: string;
  existingUsernames: Set<string>;
}) {
  const [open, setOpen] = useState(false);
  const [unityUsername, setUnityUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const createAssignment = useCreateIntercomAssignment();

  const handleAdd = () => {
    if (!displayName.trim() && !unityUsername.trim()) return;
    createAssignment.mutate(
      {
        projectID,
        unityUsername: unityUsername.trim() || null,
        displayName: displayName.trim() || null,
      },
      {
        onSuccess: () => {
          setUnityUsername("");
          setDisplayName("");
          setOpen(false);
        },
      },
    );
  };

  const usernameConflict =
    !!unityUsername.trim() && existingUsernames.has(unityUsername.trim());

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <UserPlus className="h-4 w-4 mr-1" />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add User</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Display name *"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
          />
          <Input
            placeholder="Unity username (optional)"
            value={unityUsername}
            onChange={(e) => setUnityUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdd();
            }}
          />
          <Button
            onClick={handleAdd}
            disabled={
              (!displayName.trim() && !unityUsername.trim()) ||
              usernameConflict ||
              createAssignment.isPending
            }
            className="w-full"
          >
            Add User
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Editable Username Cell ---

function EditableUsernameCell({
  assignment,
  onUpdate,
}: {
  assignment: IntercomAssignment;
  onUpdate: (id: string, username: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(assignment.unityUsername ?? "");

  const handleBlur = () => {
    setEditing(false);
    if (value.trim() && value !== (assignment.unityUsername ?? "")) {
      onUpdate(assignment.id, value.trim());
    } else {
      setValue(assignment.unityUsername ?? "");
    }
  };

  if (editing) {
    return (
      <Input
        className="h-6 w-36 text-sm font-mono"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleBlur();
          if (e.key === "Escape") {
            setValue(assignment.unityUsername ?? "");
            setEditing(false);
          }
        }}
        autoFocus
        placeholder="Set username..."
      />
    );
  }

  if (!assignment.unityUsername) {
    return (
      <button
        className="text-sm text-muted-foreground/60 italic hover:text-primary"
        onClick={() => setEditing(true)}
      >
        Set username...
      </button>
    );
  }

  return (
    <button
      className="font-mono text-sm font-semibold tracking-tight text-foreground hover:text-primary"
      onClick={() => setEditing(true)}
    >
      {assignment.unityUsername}
    </button>
  );
}

// --- Editable Channel Number ---

function EditableChannelNumber({ channel }: { channel: IntercomChannel }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(
    channel.channelNumber != null ? String(channel.channelNumber) : "",
  );
  const updateChannel = useUpdateIntercomChannel();

  const handleBlur = () => {
    setEditing(false);
    const parsed = value.trim() ? parseInt(value.trim(), 10) : null;
    if (parsed !== (channel.channelNumber ?? null)) {
      updateChannel.mutate({ id: channel.id, channelNumber: parsed });
    } else {
      setValue(
        channel.channelNumber != null ? String(channel.channelNumber) : "",
      );
    }
  };

  if (editing) {
    return (
      <Input
        className="h-7 w-16 text-xs font-mono text-center"
        type="number"
        min={1}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleBlur();
          if (e.key === "Escape") {
            setValue(
              channel.channelNumber != null
                ? String(channel.channelNumber)
                : "",
            );
            setEditing(false);
          }
        }}
        autoFocus
      />
    );
  }

  return (
    <button
      className="text-xs font-mono text-muted-foreground hover:text-foreground min-w-[2rem] text-center"
      onClick={() => setEditing(true)}
      title="Channel number (input/output)"
    >
      {channel.channelNumber != null ? `#${channel.channelNumber}` : "#–"}
    </button>
  );
}

// --- Channel Card ---

function ChannelCard({ channel }: { channel: IntercomChannel }) {
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [channelName, setChannelName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description ?? "");
  const updateChannel = useUpdateIntercomChannel();
  const deleteChannel = useDeleteIntercomChannel();

  const handleBlurName = () => {
    setEditingName(false);
    if (channelName.trim() && channelName !== channel.name) {
      updateChannel.mutate({ id: channel.id, name: channelName.trim() });
    } else {
      setChannelName(channel.name);
    }
  };

  const handleBlurDescription = () => {
    setEditingDescription(false);
    const trimmed = description.trim();
    if (trimmed !== (channel.description ?? "")) {
      updateChannel.mutate({
        id: channel.id,
        description: trimmed || null,
      });
    } else {
      setDescription(channel.description ?? "");
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between gap-2">
          <EditableChannelNumber channel={channel} />
          <div className="flex-1 min-w-0 space-y-1">
            {editingName ? (
              <Input
                className="h-7 w-48 text-sm font-semibold"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value)}
                onBlur={handleBlurName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleBlurName();
                  if (e.key === "Escape") {
                    setChannelName(channel.name);
                    setEditingName(false);
                  }
                }}
                autoFocus
              />
            ) : (
              <h4
                className="text-sm font-medium cursor-pointer hover:text-primary"
                onClick={() => setEditingName(true)}
              >
                {channel.name}
              </h4>
            )}
            {editingDescription ? (
              <Textarea
                className="text-sm min-h-[60px]"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={handleBlurDescription}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setDescription(channel.description ?? "");
                    setEditingDescription(false);
                  }
                }}
                placeholder="Describe how this channel should be used..."
                autoFocus
              />
            ) : (
              <p
                className="text-sm text-muted-foreground cursor-pointer hover:text-foreground whitespace-pre-wrap"
                onClick={() => setEditingDescription(true)}
              >
                {channel.description || "Click to add a description..."}
              </p>
            )}
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 shrink-0"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Channel</AlertDialogTitle>
                <AlertDialogDescription>
                  This will delete &quot;{channel.name}&quot;. This action
                  cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteChannel.mutate(channel.id)}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

// --- Profile Editor ---

function ProfileEditor({
  profile,
  channels,
}: {
  profile: IntercomProfile;
  channels: IntercomChannel[];
}) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(profile.name);
  const updateProfile = useUpdateIntercomProfile();
  const deleteProfile = useDeleteIntercomProfile();

  const config = (profile.channelConfig ?? {}) as Record<
    string,
    { talk: boolean; listen: boolean }
  >;

  const handleBlurName = () => {
    setEditingName(false);
    if (name.trim() && name !== profile.name) {
      updateProfile.mutate({ id: profile.id, name: name.trim() });
    } else {
      setName(profile.name);
    }
  };

  const toggleChannel = (channelId: string, field: "talk" | "listen") => {
    const current = config[channelId] ?? { talk: false, listen: false };
    const updated = {
      ...config,
      [channelId]: { ...current, [field]: !current[field] },
    };
    updateProfile.mutate({ id: profile.id, channelConfig: updated });
  };

  return (
    <Card>
      <CardContent className="pt-3 pb-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          {editingName ? (
            <Input
              className="h-7 w-48 text-sm font-semibold"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={handleBlurName}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleBlurName();
                if (e.key === "Escape") {
                  setName(profile.name);
                  setEditingName(false);
                }
              }}
              autoFocus
            />
          ) : (
            <h4
              className="text-sm font-medium cursor-pointer hover:text-primary"
              onClick={() => setEditingName(true)}
            >
              {profile.name}
            </h4>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Profile</AlertDialogTitle>
                <AlertDialogDescription>
                  This will delete &quot;{profile.name}&quot; and unassign all
                  users using it.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => deleteProfile.mutate(profile.id)}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        {channels.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Add channels first to configure talk/listen.
          </p>
        ) : (
          <div className="grid gap-1">
            {channels.map((ch) => {
              const s = config[ch.id] ?? { talk: false, listen: false };
              return (
                <div
                  key={ch.id}
                  className="flex items-center gap-3 text-xs py-0.5"
                >
                  <span className="w-24 truncate text-muted-foreground">
                    {ch.name}
                  </span>
                  <button
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${s.talk ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" : "text-muted-foreground/40 hover:text-muted-foreground"}`}
                    onClick={() => toggleChannel(ch.id, "talk")}
                  >
                    <Mic className="h-3 w-3" />T
                  </button>
                  <button
                    className={`flex items-center gap-1 px-1.5 py-0.5 rounded ${s.listen ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" : "text-muted-foreground/40 hover:text-muted-foreground"}`}
                    onClick={() => toggleChannel(ch.id, "listen")}
                  >
                    <Volume2 className="h-3 w-3" />L
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Main Component ---

export function IntercomTab({ projectId, studioId }: IntercomTabProps) {
  const [newChannelName, setNewChannelName] = useState("");
  const [newProfileName, setNewProfileName] = useState("");
  const { data: channels, isLoading: channelsLoading } =
    useProjectIntercomChannels(projectId);
  const { data: assignments, isLoading: assignmentsLoading } =
    useProjectIntercomAssignments(projectId);
  const { data: profiles, isLoading: profilesLoading } =
    useProjectIntercomProfiles(projectId);
  const { data: studio } = useSingleStudio(studioId ?? undefined);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const createChannel = useCreateIntercomChannel();
  const createProfile = useCreateIntercomProfile();
  const updateAssignment = useUpdateIntercomAssignment();
  const deleteAssignment = useDeleteIntercomAssignment();
  const bulkAssign = useBulkAssignIntercomProfile();

  const toggleUser = (id: string) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllUsers = () => {
    if (selectedUsers.size === sortedAssignments.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(sortedAssignments.map((a) => a.id)));
    }
  };

  const handleBulkAssignProfile = (profileID: string | null) => {
    bulkAssign.mutate(
      { profileID, assignmentIDs: Array.from(selectedUsers) },
      { onSuccess: () => setSelectedUsers(new Set()) },
    );
  };

  const existingUsernames = useMemo(
    () =>
      new Set(
        assignments.map((a) => a.unityUsername).filter((u): u is string => !!u),
      ),
    [assignments],
  );

  const existingPersonIDs = useMemo(
    () =>
      new Set(
        assignments.map((a) => a.personID).filter((id): id is string => !!id),
      ),
    [assignments],
  );

  const sortedAssignments = useMemo(
    () =>
      [...assignments].sort((a, b) =>
        getAssignmentSortName(a).localeCompare(getAssignmentSortName(b)),
      ),
    [assignments],
  );

  const handleAddChannel = () => {
    if (!newChannelName.trim()) return;
    const maxSortOrder = Math.max(0, ...channels.map((c) => c.sortOrder));
    createChannel.mutate(
      {
        projectID: projectId,
        name: newChannelName.trim(),
        sortOrder: maxSortOrder + 1,
      },
      { onSuccess: () => setNewChannelName("") },
    );
  };

  const handleAddProfile = () => {
    if (!newProfileName.trim()) return;
    const config: Record<string, { talk: boolean; listen: boolean }> = {};
    for (const ch of channels) {
      config[ch.id] = { talk: true, listen: true };
    }
    createProfile.mutate(
      {
        projectID: projectId,
        name: newProfileName.trim(),
        channelConfig: config,
      },
      { onSuccess: () => setNewProfileName("") },
    );
  };

  const [exporting, setExporting] = useState(false);

  const handleExportUnity = async () => {
    setExporting(true);
    try {
      const res = await apiFetch(`/api/intercom-export/${projectId}/unity`);
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "intercom-config.unity";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const isLoading = channelsLoading || assignmentsLoading || profilesLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
          <Headphones className="h-4 w-4 shrink-0" />
          <span className="truncate">
            Unity Server:{" "}
            <span className="font-mono text-foreground">
              {studio?.unity_hostname ||
                (studio ? "No hostname configured" : "No studio selected")}
            </span>
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="self-end sm:self-auto shrink-0"
          onClick={handleExportUnity}
          disabled={
            exporting || (assignments.length === 0 && channels.length === 0)
          }
        >
          <Download className="h-4 w-4 mr-1" />
          Export .unity
        </Button>
      </div>

      {/* Users Section */}
      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-sm font-medium">Users</h3>
          <div className="flex flex-wrap gap-2">
            <BulkAddDialog
              projectID={projectId}
              existingPersonIDs={existingPersonIDs}
              profiles={profiles}
            />
            <AddSingleUserDialog
              projectID={projectId}
              existingUsernames={existingUsernames}
            />
          </div>
        </div>
        {selectedUsers.size > 0 && (
          <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-1.5">
            <span className="text-sm text-muted-foreground">
              {selectedUsers.size} selected
            </span>
            <Select
              value="__action__"
              onValueChange={(v) => {
                if (v !== "__action__") {
                  handleBulkAssignProfile(v === "__none__" ? null : v);
                }
              }}
            >
              <SelectTrigger className="h-7 w-40 text-xs">
                <SelectValue placeholder="Assign profile..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__action__" disabled>
                  Assign profile...
                </SelectItem>
                <SelectItem value="__none__">No profile (clear)</SelectItem>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={() => setSelectedUsers(new Set())}
            >
              Clear
            </Button>
          </div>
        )}
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8 py-1.5">
                  {sortedAssignments.length > 0 && (
                    <Checkbox
                      checked={
                        selectedUsers.size === sortedAssignments.length &&
                        sortedAssignments.length > 0
                      }
                      onCheckedChange={toggleAllUsers}
                    />
                  )}
                </TableHead>
                <TableHead className="py-1.5">Name</TableHead>
                <TableHead className="py-1.5">Username</TableHead>
                <TableHead className="py-1.5 hidden sm:table-cell">
                  Profile
                </TableHead>
                <TableHead className="w-8 py-1.5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedAssignments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-4 text-muted-foreground text-sm"
                  >
                    No intercom users added yet
                  </TableCell>
                </TableRow>
              ) : (
                sortedAssignments.map((assignment) => (
                  <TableRow key={assignment.id} className="h-9">
                    <TableCell className="py-1">
                      <Checkbox
                        checked={selectedUsers.has(assignment.id)}
                        onCheckedChange={() => toggleUser(assignment.id)}
                      />
                    </TableCell>
                    <TableCell className="py-1 max-w-[120px] sm:max-w-none truncate">
                      {assignment.person ? (
                        <PersonHoverCard
                          personId={assignment.person.id}
                          firstName={assignment.person.firstName}
                          lastName={assignment.person.lastName}
                          personType={assignment.person.personType}
                          phone={assignment.person.phone}
                        >
                          <span className="text-sm cursor-pointer hover:underline">
                            {assignment.person.firstName}{" "}
                            {assignment.person.lastName}
                          </span>
                        </PersonHoverCard>
                      ) : (
                        <span className="text-sm">
                          {assignment.displayName || assignment.unityUsername}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="py-1">
                      <EditableUsernameCell
                        assignment={assignment}
                        onUpdate={(id, un) =>
                          updateAssignment.mutate({ id, unityUsername: un })
                        }
                      />
                    </TableCell>
                    <TableCell className="py-1 hidden sm:table-cell">
                      <Select
                        value={assignment.profileID ?? "__none__"}
                        onValueChange={(v) =>
                          updateAssignment.mutate({
                            id: assignment.id,
                            profileID: v === "__none__" ? null : v,
                          })
                        }
                      >
                        <SelectTrigger className="h-7 w-32 text-xs">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {profiles.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="py-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => deleteAssignment.mutate(assignment.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Profiles Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Profiles
        </h3>
        {profiles.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No profiles configured yet. Profiles define per-channel talk/listen
            permissions.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {profiles.map((profile) => (
              <ProfileEditor
                key={profile.id}
                profile={profile}
                channels={channels}
              />
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="New profile name..."
            value={newProfileName}
            onChange={(e) => setNewProfileName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddProfile();
            }}
          />
          <Button
            className="shrink-0"
            onClick={handleAddProfile}
            disabled={!newProfileName.trim() || createProfile.isPending}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Profile
          </Button>
        </div>
      </div>

      {/* Channels Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium flex items-center gap-2">
          <Radio className="h-4 w-4" />
          Channels
        </h3>
        {channels.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No channels configured yet.
          </p>
        ) : (
          <div className="space-y-3">
            {channels.map((channel) => (
              <ChannelCard key={channel.id} channel={channel} />
            ))}
          </div>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            placeholder="New channel name..."
            value={newChannelName}
            onChange={(e) => setNewChannelName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAddChannel();
            }}
          />
          <Button
            className="shrink-0"
            onClick={handleAddChannel}
            disabled={!newChannelName.trim() || createChannel.isPending}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Channel
          </Button>
        </div>
      </div>
    </div>
  );
}
